import { Button, Checkbox, DatePicker, Select, Space, Table, Tag, Tooltip, Typography } from 'antd'
import dayjs, { Dayjs } from 'dayjs'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  FOLLOW_UP_STATUS_COLOR,
  followUpStatusLabel,
  FollowUpStatus,
  InvoiceRefundStatus,
  REFUND_CHANNEL_LABEL,
  REFUND_REASON_LABEL,
  REFUND_STATUS_COLOR,
  REFUND_STATUS_LABEL,
  RefundChannel,
  RefundLedgerRow,
  RefundReason,
} from '../../../../../types/refund'
import { overdueBusinessDays, REFUND_DEADLINE_BUSINESS_DAYS } from '../../../../../utils/businessDays'
import RefundDetailDrawer from './RefundDetailDrawer'
import RefundHistory from './RefundHistory'

type Props = {
  rows: RefundLedgerRow[]
  workspaceId: number
  loading?: boolean
  /** 결제 건 목록의 `기록 보기` 가 지목한 행. 해당 행으로 스크롤하고 강조한다 */
  highlightRefundId?: number | null
}

type Filters = {
  status?: InvoiceRefundStatus
  reason?: RefundReason
  channel?: RefundChannel
  range?: [Dayjs, Dayjs] | null
  mismatchOnly: boolean
}

const money = (value?: number | null) => (value == null ? '-' : `${Number(value).toLocaleString()}원`)
const datetime = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-')
const date = (value?: string | null) => (value ? dayjs(value).format('YYYY-MM-DD') : '-')

const options = <T extends string>(labels: Record<T, string>) =>
  (Object.keys(labels) as T[]).map((value) => ({ value, label: labels[value] }))

/** 사건 묶음 위치. 1행 사건은 표시하지 않는다 — 모든 행에 (1/1) 이 붙으면 묶음이 눈에 안 띈다 */
const pairLabel = (row: RefundLedgerRow) =>
  row.pairSequence && row.pairSize > 1 ? `(${row.pairSequence}/${row.pairSize})` : null

/**
 * 펼친 행의 상태 전이 이력. 누가 언제 무엇을 했는지가 대장의 존재 이유다.
 *
 * 렌더 안에서 정의하면 매 렌더마다 새 컴포넌트 타입이 되어 펼친 행이 통째로 다시 마운트된다.
 */
const renderDetail = (row: RefundLedgerRow) => <RefundHistory row={row} />

export default function RefundLedgerTable({ rows, workspaceId, loading, highlightRefundId }: Props) {
  const [filters, setFilters] = useState<Filters>({ mismatchOnly: false })
  /** 상세 서랍이 연 행. 대장이 다시 불려도 같은 행을 가리키도록 id 로 들고 본문은 rows 에서 찾는다 */
  const [detailRefundId, setDetailRefundId] = useState<number | null>(null)

  // 🔴 서랍이 `row` 객체를 통째로 들고 있으면 확정 직후 무효화로 대장이 갱신돼도 서랍은 낡은
  // 상태를 보여준다. id 로 들고 매 렌더마다 최신 행을 집는다.
  const detailRow = rows.find((row) => row.refundId === detailRefundId) ?? null

  /**
   * 정정 연결의 **반대 방향** (화면 명세 8.4 · aivatar #502).
   *
   * 서버는 `supersedesRefundId` 한 방향만 준다 — 정정 행이 원 행을 가리킨다. 원 행에서 "나는
   * 정정됐다" 를 알려면 대장 전체를 훑어야 하고, 그 목록을 들고 있는 것은 이 표뿐이다.
   */
  const supersededBy = useMemo(() => {
    const map = new Map<number, number>()
    rows.forEach((row) => {
      if (row.supersedesRefundId) map.set(row.supersedesRefundId, row.refundId)
    })
    return map
  }, [rows])

  const filtered = useMemo(
    () =>
      rows.filter((row) => {
        if (filters.status && row.status !== filters.status) return false
        if (filters.reason && row.reason !== filters.reason) return false
        if (filters.channel && row.channel !== filters.channel) return false
        if (filters.mismatchOnly && !row.amountMismatch) return false
        if (filters.range) {
          // 기간은 **접수 시각** 기준이다. 처리일로 걸면 아직 처리되지 않은 행이 전부 빠져
          // "이 기간에 들어온 환불" 을 묻는 질문에 답하지 못한다.
          const at = dayjs(row.createdAt)
          if (at.isBefore(filters.range[0].startOf('day'))) return false
          if (at.isAfter(filters.range[1].endOf('day'))) return false
        }
        return true
      }),
    // 🔴 여기서 정렬하지 않는다. 서버가 "접수·보류 먼저 → 최신순" 으로 이미 정렬해 내린다.
    // 화면에서 다시 정렬하면 규칙이 두 곳에 생겨 한쪽만 바뀔 때 조용히 어긋난다.
    [rows, filters],
  )

  return (
    <div style={{ marginBottom: 20 }}>
      <Typography.Title level={4}>환불대장</Typography.Title>

      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear
          placeholder="상태"
          style={{ width: 140 }}
          options={options(REFUND_STATUS_LABEL)}
          value={filters.status}
          onChange={(status) => setFilters((f) => ({ ...f, status }))}
        />
        <Select
          allowClear
          placeholder="환불 유형"
          style={{ width: 200 }}
          options={options(REFUND_REASON_LABEL)}
          value={filters.reason}
          onChange={(reason) => setFilters((f) => ({ ...f, reason }))}
        />
        <Select
          allowClear
          placeholder="처리 경로"
          style={{ width: 180 }}
          options={options(REFUND_CHANNEL_LABEL)}
          value={filters.channel}
          onChange={(channel) => setFilters((f) => ({ ...f, channel }))}
        />
        <DatePicker.RangePicker
          placeholder={['접수 시작일', '접수 종료일']}
          value={filters.range ?? null}
          onChange={(range) => setFilters((f) => ({ ...f, range: range as Filters['range'] }))}
        />
        <Checkbox
          checked={filters.mismatchOnly}
          onChange={(e) => setFilters((f) => ({ ...f, mismatchOnly: e.target.checked }))}
        >
          금액 불일치만
        </Checkbox>
        <Typography.Text type="secondary">
          {filtered.length} / {rows.length} 건
        </Typography.Text>
      </Space>

      <Table
        dataSource={filtered}
        rowKey={(row: RefundLedgerRow) => row.refundId}
        loading={loading}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
        onRow={(row: RefundLedgerRow) => ({
          id: `refund-row-${row.refundId}`,
          style:
            highlightRefundId === row.refundId ? { background: '#fffbe6', outline: '2px solid #faad14' } : undefined,
        })}
        expandable={{
          expandedRowRender: renderDetail,
          rowExpandable: (row) => row.history.length > 0 || !!row.followUpDetail || !!row.memo,
        }}
        columns={[
          {
            title: '상태',
            key: 'status',
            render: (t, row: RefundLedgerRow) => {
              const overdue = overdueBusinessDays(row.createdAt, row.status === 'COMPLETED' || row.status === 'WITHDRAWN')
              return (
                <Space direction="vertical" size={2}>
                  <Tag color={REFUND_STATUS_COLOR[row.status]}>{REFUND_STATUS_LABEL[row.status] ?? row.status}</Tag>
                  {row.supersedesRefundId ? (
                    <Tooltip title={`철회된 기록 #${row.supersedesRefundId} 을 정정한 행입니다`}>
                      <Tag color="blue">정정 (원 #{row.supersedesRefundId})</Tag>
                    </Tooltip>
                  ) : null}
                  {supersededBy.has(row.refundId) ? (
                    <Tooltip title="유효한 기록은 정정 행입니다. 이 행의 금액으로 대사하지 마세요">
                      <Tag>#{supersededBy.get(row.refundId)} 로 정정됨</Tag>
                    </Tooltip>
                  ) : null}
                  {overdue ? (
                    <Tooltip title={`처리 기한 ${REFUND_DEADLINE_BUSINESS_DAYS}영업일 초과 (공휴일 미반영)`}>
                      <Tag color="red">{overdue}영업일 경과</Tag>
                    </Tooltip>
                  ) : null}
                </Space>
              )
            },
          },
          {
            title: '환불 유형',
            key: 'reason',
            render: (t, row: RefundLedgerRow) => (
              <Space direction="vertical" size={2}>
                <span>{REFUND_REASON_LABEL[row.reason] ?? row.reason}</span>
                {pairLabel(row) ? (
                  <Tooltip title={`사건 묶음 ${row.refundGroupNo ?? ''}`}>
                    <Tag>{pairLabel(row)}</Tag>
                  </Tooltip>
                ) : null}
                {/* 서버 판정이다. 짝이 확정되기 전에는 후속 조치가 발화하지 않는다 */}
                {row.awaitingPair ? (
                  <Tooltip title="같은 사건의 다른 행이 아직 처리 완료가 아닙니다. 전부 확정돼야 후속 조치가 실행됩니다">
                    <Tag color="gold">짝 대기</Tag>
                  </Tooltip>
                ) : null}
              </Space>
            ),
          },
          {
            title: '대상 결제 건',
            key: 'invoice',
            // 행이 청구서 또는 크레딧 팩 결제 건을 가리킨다(D-121). 결제고유번호·결제액·결제일은 어느
            // 쪽이든 같은 칸으로 오고, 결제대행사에서 찾을 상점(정기결제·단건)이 갈리므로 종류를 붙인다
            render: (t, row: RefundLedgerRow) => (
              <Space direction="vertical" size={0}>
                {row.paymentOrderId != null ? (
                  <Tooltip title={`크레딧 팩 결제 건 #${row.paymentOrderId} · 단건 상점 결제`}>
                    <Tag color="geekblue">크레딧 팩</Tag>
                  </Tooltip>
                ) : null}
                <span>
                  {date(row.invoicePaidOn)} · {money(row.invoicePrice)}
                </span>
                <Typography.Text type="secondary" copyable={!!row.paymentNumber}>
                  {row.paymentNumber ?? '-'}
                </Typography.Text>
                <Typography.Text type="secondary" copyable={!!row.orderNumber}>
                  {row.orderNumber ?? '-'}
                </Typography.Text>
              </Space>
            ),
          },
          {
            title: '산정액',
            key: 'amount',
            render: (t, row: RefundLedgerRow) => (
              <Space direction="vertical" size={0}>
                <span>{money(row.amount)}</span>
                {row.penalty ? (
                  <Typography.Text type="secondary">위약금 {money(row.penalty)}</Typography.Text>
                ) : null}
              </Space>
            ),
          },
          {
            title: '실제 취소액',
            key: 'actualCancelAmount',
            render: (t, row: RefundLedgerRow) => (
              <Space direction="vertical" size={2}>
                <span>{money(row.actualCancelAmount)}</span>
                {/* 산정액 ≠ 실지급액. 서버가 판정해 내리는 값이라 화면에서 다시 비교하지 않는다 */}
                {row.amountMismatch ? <Tag color="red">불일치</Tag> : null}
              </Space>
            ),
          },
          { title: '처리일', key: 'refundedAt', render: (t, row: RefundLedgerRow) => datetime(row.refundedAt) },
          {
            /**
             * 🔴 "처리자" 가 아니라 **접수자**다. 서버가 채우는 값은 접수 시점의 담당자이고
             * (`issuerId`·`issuerEmail`), 대장 응답에는 확정한 사람이 따로 없다. 종전 제목대로
             * 두면 처리일·처리 경로·실제 취소액이 비어 있는 접수 행도 사람 이름만 차 있어
             * 목록만 보고 이미 처리된 것으로 읽힌다(화면 구성 v3 S5).
             */
            title: (
              <Tooltip title="환불을 접수한 담당자입니다. 확정한 사람은 행을 펼쳐 처리 이력에서 확인하세요">
                접수자
              </Tooltip>
            ),
            key: 'issuer',
            render: (t, row: RefundLedgerRow) =>
              row.issuerId ? <Link to={`/user/${row.issuerId}`}>{row.issuerEmail}</Link> : (row.issuerEmail ?? '-'),
          },
          {
            title: '처리 경로',
            key: 'channel',
            render: (t, row: RefundLedgerRow) => (row.channel ? REFUND_CHANNEL_LABEL[row.channel] : '-'),
          },
          {
            title: '동작',
            key: 'actions',
            render: (t, row: RefundLedgerRow) => (
              <Button size="small" type="primary" onClick={() => setDetailRefundId(row.refundId)}>
                상세
              </Button>
            ),
          },
          {
            title: '후속 조치',
            key: 'followUp',
            render: (t, row: RefundLedgerRow) => {
              const status = row.followUpStatus as FollowUpStatus | null
              if (!status) return '-'
              return (
                <Tooltip title={row.followUpDetail ?? undefined}>
                  <Space direction="vertical" size={0}>
                    <Tag color={FOLLOW_UP_STATUS_COLOR[status]}>{followUpStatusLabel(status, row.paymentOrderId)}</Tag>
                    {row.followUpAt ? (
                      <Typography.Text type="secondary">{datetime(row.followUpAt)}</Typography.Text>
                    ) : null}
                  </Space>
                </Tooltip>
              )
            },
          },
        ]}
      />

      <RefundDetailDrawer
        open={!!detailRow}
        row={detailRow}
        workspaceId={workspaceId}
        supersededByRefundId={detailRow ? (supersededBy.get(detailRow.refundId) ?? null) : null}
        // 대장에 없는 행(다른 워크스페이스·페이지 밖)은 열 수 없으므로 그때는 링크를 걸지 않는다
        onOpenRefund={(refundId) =>
          rows.some((row) => row.refundId === refundId) ? setDetailRefundId(refundId) : undefined
        }
        onClose={() => setDetailRefundId(null)}
      />
    </div>
  )
}
